import React, { useEffect, useRef, useState } from 'react'
import Loading from './Loading'
import { useUser } from './UserProvider'

interface VertelierExperienceProp {
  normalTitle: string
  italicTitle?: string
  page?: string
}

const experienceData = [
  {
    icon: '/ic_shipping.svg',
    title: 'Free',
    italicTitle: 'Shipping',
    description:
      'Enjoy free shipping on every order, ensuring a seamless experience as we deliver luxury right to your doorstep.',
    iconClassName: 'w-[93px] h-full object-center',
    mobileDescription:
      'Enjoy free shipping on every order, ensuring a seamless experience.',
    mobileIconClassName: 'w-auto h-[72px] object-center',
    zIndex: 40,
  },
  {
    icon: '/ic_resizing.svg',
    title: 'Free',
    italicTitle: 'Resizing',
    description:
      'We are pleased to offer an annual cleaning, resizing and a maintainance service.',
    iconClassName: 'h-[72px] w-auto',
    mobileDescription:
      'We are pleased to offer an annual cleaning, resizing and a maintainance service.',
    mobileIconClassName: 'h-[62px] w-auto',
    zIndex: 30,
  },
  {
    icon: '/ic_return.svg',
    title: '30 Days',
    italicTitle: 'Returns',
    description:
      'Return your purchase within 100 days for a full refund or exchange, providing flexibility and peace of mind.',
    iconClassName: 'h-[72px] w-auto',
    mobileDescription:
      'Return your purchase within 100 days for a full refund or exchange, providing flexibility and peace of mind.',
    mobileIconClassName: 'h-[72px] w-auto',
    zIndex: 20,
  },
  {
    icon: '/ic_engraving.svg',
    title: 'Complimentary',
    italicTitle: 'Engraving',
    description:
      'Enjoy the option to upgrade your jewelry yearly, keeping your collection fresh and aligned with your evolving style.',
    iconClassName: 'h-[72px] w-auto',
    mobileDescription:
      'Enjoy the option to upgrade your jewelry yearly, keeping your collection fresh and aligned with your evolving style.',
    mobileIconClassName: 'relative h-[72px] w-auto',
    zIndex: 10,
  },
]

const experienceDataHome = [
  {
    icon: '/home/promises/Union.png',
    title: 'Exclusive Buyback',
    italicTitle: 'Perks',
    description:
      'Benefit from profitable returns when upgrading, clearly proving our jewelry’s ongoing worth',
    iconClassName: 'w-[93px] h-full',
    mobileDescription:
      'Benefit from profitable returns when upgrading, clearly proving our jewelry’s ongoing worth',
    mobileIconClassName: 'w-auto h-[72px] object-center',
    zIndex: 40,
  },
  {
    icon: '/home/promises/client-advisor.png',
    title: 'Dedicated',
    italicTitle: 'Client Advisors',
    description:
      'Your personal consultant ensures confident decisions, insider recommendations, and effortless ring selection.',
    iconClassName: 'h-auto w-[93px]',
    mobileDescription:
      'Your personal consultant ensures confident decisions, insider recommendations, and effortless ring selection.',
    mobileIconClassName: 'h-[62px] w-auto',
    zIndex: 30,
  },
  {
    icon: '/home/promises/unmatched.png',
    title: 'Unmatched',
    italicTitle: 'Customization',
    description:
      'Refine each detail, from precious metal to gemstone hue, and confidently seize ultimate personalization.',
    iconClassName: 'h-auto w-[93px]',
    mobileDescription:
      'Refine each detail, from precious metal to gemstone hue, and confidently seize ultimate personalization.',
    mobileIconClassName: 'h-[72px] w-auto',
    zIndex: 20,
  },
  {
    icon: '/home/promises/complimentary.png ',
    title: 'Complimentary',
    italicTitle: 'Annual Enhancement',
    description:
      'Receive professional polishing yearly, maintaining impeccable finish and confidently showcasing everlasting glow.',
    iconClassName: 'h-auto w-[93px]',
    mobileDescription:
      'Receive professional polishing yearly, maintaining impeccable finish and confidently showcasing everlasting glow.',
    mobileIconClassName: 'relative h-[72px] w-auto',
    zIndex: 10,
  },
  {
    icon: '/home/promises/global-delivery.png',
    title: 'Free',
    italicTitle: 'Global Delivery',
    description:
      'Complimentary worldwide shipping on all orders.',
    iconClassName: 'h-auto w-[93px]',
    mobileDescription:
      'Complimentary worldwide shipping on all orders.',
    mobileIconClassName: 'relative h-[72px] w-auto',
    zIndex: 10,
  },
]

const VertelierExperienceEngagement = ({
  normalTitle,
  italicTitle,
  page = 'default',
}: VertelierExperienceProp) => {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null)
  const scrollContainerRef = useRef<HTMLDivElement | null>(null)
  const isHome = page === 'home'
  const { userPageLoading } = useUser()

  let data = isHome ? experienceDataHome : experienceData

  const containerStyle = true
    ? ({
        backgroundImage: 'url("/essyou/Vertelier_promises_bg.png")',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        position: 'relative',
      } as React.CSSProperties)
    : {}

  const [isInView, setIsInView] = useState(false)
  const [isTouching, setIsTouching] = useState(false)

  const scrollDirection = useRef(1)
  const speed = useRef(2)
  const interval = useRef<any>(null)

  const lastTouchX = useRef(0)

  const startAutoScroll = () => {
    const scrollContainer = scrollContainerRef.current
    if (scrollContainer && window.innerWidth < 940) {
      interval.current = setInterval(() => {
        const step = speed.current * scrollDirection.current

        requestAnimationFrame(() => {
          scrollContainer.scrollLeft += step
        })

        if (
          scrollContainer.scrollLeft + scrollContainer.clientWidth >=
          scrollContainer.scrollWidth
        ) {
          scrollDirection.current = -1
        } else if (scrollContainer.scrollLeft <= 0) {
          scrollDirection.current = 1
        }
      }, 20)
    }
  }

  useEffect(() => {
    const scrollContainer = scrollContainerRef.current

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0]
        if (entry.isIntersecting) {
          setIsInView(true)
        } else {
          setIsInView(false)
        }
      },
      { threshold: 0.1 }
    )

    if (scrollContainer) {
      observer.observe(scrollContainer)
    }

    if (isInView && !isTouching) {
      startAutoScroll()
    }

    return () => {
      observer.disconnect()
      clearInterval(interval.current)
    }
  }, [isInView, isTouching])

  useEffect(() => {
    const scrollContainer = scrollContainerRef.current

    const handleTouchStart = (e: TouchEvent) => {
      setIsTouching(true)
      clearInterval(interval.current)

      if (e.touches.length > 0) {
        lastTouchX.current = e.touches[0].clientX
      }
    }

    const handleTouchEnd = () => {
      setIsTouching(false)
      if (isInView) {
        startAutoScroll()
      }
    }

    const handleTouchMove = (e: TouchEvent) => {
      if (scrollContainer && e.touches.length > 0) {
        const touch = e.touches[0]
        const delta = lastTouchX.current - touch.clientX
        scrollContainer.scrollLeft += delta
        lastTouchX.current = touch.clientX
      }
    }

    if (scrollContainer) {
      scrollContainer.addEventListener('touchstart', handleTouchStart)
      scrollContainer.addEventListener('touchend', handleTouchEnd)
      scrollContainer.addEventListener('touchmove', handleTouchMove)
    }

    return () => {
      if (scrollContainer) {
        scrollContainer.removeEventListener('touchstart', handleTouchStart)
        scrollContainer.removeEventListener('touchend', handleTouchEnd)
        scrollContainer.removeEventListener('touchmove', handleTouchMove)
      }
    }
  }, [isInView])

  return userPageLoading ? (
    <div>
      <Loading
        classes={
          'flex-grow flex relative max-h-screen mt-[25px] justify-center mx-[40%] h-[35px] !bg-[rgba(128,128,128,0.3)] !text-white'
        }
      />

      <div className='flex justify-center p-4 bg-black mt-[20px]'>
        <div className='w-36 h-36 !bg-[rgba(128,128,128,0.3)] rounded-full border border-gray-900 animate-pulse'></div>
        <div className='w-36 h-36 !bg-[rgba(128,128,128,0.3)] rounded-full border border-gray-900 animate-pulse'></div>
        <div className='w-36 h-36 !bg-[rgba(128,128,128,0.3)] rounded-full border border-gray-900 animate-pulse'></div>
        <div className='w-36 h-36 !bg-[rgba(128,128,128,0.3)] rounded-full border border-gray-900 animate-pulse'></div>
      </div>
    </div>
  ) : (
    <div style={containerStyle} className='relative w-full'>
      <div
        className={`${
          isHome ? 'text-black relative z-10' : 'text-white'
        } py-5 switch:py-10 mb-[40px]`}
      >
        <div className='max-w-[1440px] mx-auto text-center'>
          <h1 className='hidden xlg:block font-cambonLight text-heading mb-lg:text-[26px] sm:text-[28px] switch:text-[30px] xlg:text-[52px] lg:text-[52px] text-[#A08260] leading-[56px] text-center mb-10'>
            {normalTitle}{' '}
            <span className='font-cambonLightItalic'>{italicTitle}</span>
          </h1>

          <div className='xlg:hidden'>
            <h1 className='font-cambonLight text-[30px] lg:text-[48px] text-[#A08260] leading-[36px] text-center'>
              {normalTitle}{' '}
              <span className='font-cambonLightItalic'>{italicTitle}</span>
            </h1>
          </div>

          <div className='hidden xlg:grid xlg:grid-cols-6 gap-[190px] justify-center relative z-1 pb-10'>
            <div
              className={`relative h-[309px] w-[309px] ${
                isHome ? 'sm:-ml-[260px]' : 'sm:-ml-16'
              }`}
            >
              <DesktopCircleBorder isHome={isHome} />
            </div>
            <div className='flex absolute left-1/2 -translate-x-1/2 sm:ml-32'>
              {data.map((item, index) => (
                <div
                  key={index}
                  className={`
                    relative
                    -ml-8 first:ml-0
                    transition-all duration-300 ease-in-out
                    ${
                      hoveredIndex === index
                        ? 'z-20 scale-105'
                        : 'z-10 hover:z-20'
                    }
                    ${
                      hoveredIndex !== null && hoveredIndex !== index
                        ? 'opacity-100'
                        : 'opacity-100'
                    }
                  `}
                  onMouseEnter={() => setHoveredIndex(index)}
                  onMouseLeave={() => setHoveredIndex(null)}
                >
                  <ExperienceCircle
                    icon={item.icon}
                    title={item.title}
                    italicTitle={item.italicTitle}
                    description={item.description}
                    iconClassName={item.iconClassName}
                    isHome={isHome}
                  />
                </div>
              ))}
              <div className='relative h-[309px] w-[309px] -ml-8'>
                <DesktopCircleBorder isHome={isHome} />
              </div>
            </div>
          </div>

          <div className='xlg:hidden relative pt-16 pb-5 overflow-x-hidden'>
            <div
              className='relative flex overflow-x-auto pb-8 hide-scrollbar'
              ref={scrollContainerRef}
            >
              <div className={`flex ${isHome ? 'gap-0' : 'gap-0'}`}>
                {!isHome && (
                  <div
                    className={`relative h-[309px] w-[309px] ${
                      isHome ? '-mr-12' : ''
                    } flex-none`}
                  >
                    <DesktopCircleBorder isHome={isHome} />
                  </div>
                )}
                {data.map((item, index) => (
                  <div
                    key={index}
                    className={`relative flex-none ${
                      index === 0 ? '' : '-ml-8'
                    }`}
                  >
                    <ExperienceCircle
                      icon={item.icon}
                      title={item.title}
                      italicTitle={item.italicTitle}
                      description={item.description}
                      iconClassName={item.iconClassName}
                      isHome={isHome}
                    />
                  </div>
                ))}

                {!isHome && (
                  <div
                    className={`relative h-[309px] w-[309px] -ml-8 flex-none`}
                  >
                    <DesktopCircleBorder isHome={isHome} />
                  </div>
                )}
              </div>
            </div>
            <style jsx>{`
              .hide-scrollbar::-webkit-scrollbar {
                display: none;
              }
              .hide-scrollbar {
                -ms-overflow-style: none;
                scrollbar-width: none;
              }
            `}</style>
          </div>
        </div>
      </div>
    </div>
  )
}

const MobileCircleBorder = ({ isHome }: { isHome: boolean }) => (
  <svg
    className='absolute inset-0 w-full h-full'
    viewBox='0 0 273 273'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
  >
    <circle cx='136.5' cy='136.5' r='136.5' fill='transparent' />
    <circle
      cx='136.5'
      cy='136.5'
      r='136'
      stroke='url(#paint0_linear_mobile)'
      strokeWidth='2'
    />
    <defs>
      <linearGradient
        id='paint0_linear_mobile'
        x1='136.5'
        y1='0'
        x2='136.5'
        y2='273'
        gradientUnits='userSpaceOnUse'
      >
        <stop
          offset='0'
          stopColor={isHome ? 'white' : '#A08260'}
          stopOpacity='0.5'
        />
        <stop
          offset='0.3'
          stopColor={isHome ? 'white' : '#A08260'}
          stopOpacity='0.4'
        />
        <stop
          offset='0.6'
          stopColor={isHome ? 'white' : '#A08260'}
          stopOpacity='0.3'
        />
        <stop
          offset='1'
          stopColor={isHome ? 'white' : '#A08260'}
          stopOpacity='0.02'
        />
      </linearGradient>
    </defs>
  </svg>
)

const DesktopCircleBorder = ({ isHome }: { isHome: boolean }) => (
  <svg
    className='absolute inset-0 w-full h-full'
    viewBox='0 0 349 349'
    fill={isHome ? 'white' : 'black'}
    xmlns='http://www.w3.org/2000/svg'
  >
    <circle
      cx='174.5'
      cy='174.5'
      r='174'
      stroke={isHome ? 'black' : '#A08260'}
      strokeWidth='1'
      fill={isHome ? 'white' : 'black'}
    />
  </svg>
)

interface ExperienceCircleProps {
  icon: string
  title: string
  italicTitle: string
  description: string
  iconClassName: string
  isMobile?: boolean
  isHome?: boolean
}

const ExperienceCircle = ({
  icon,
  title,
  italicTitle,
  description,
  iconClassName,
  isMobile = false,
  isHome = false,
}: ExperienceCircleProps) => {
  const baseClasses = 'relative flex flex-col items-center mx-auto mb-[20px]'
  const mobileClasses = 'h-[241px] w-[240px]'
  const desktopClasses = 'h-[309px] w-[309px]'

  return (
    <div
      className={`${baseClasses} ${
        isMobile ? mobileClasses : desktopClasses
      } select-none`}
    >
      {isMobile ? (
        <MobileCircleBorder isHome={isHome} />
      ) : (
        <DesktopCircleBorder isHome={isHome} />
      )}
      <div className='flex flex-col items-center p-4 pt-10 relative'>
        <div
          className={`relative ${
            isMobile ? 'mt-2 mb-2 w-10 h-10' : 'mb-2 w-[3.5rem] h-[3.5rem]'
          }`}
        >
          <img
            src={icon}
            className={`${iconClassName} ${isMobile ? 'scale-99' : ''}`}
            alt=''
          />
        </div>
        <div className='relative z-[998]'>
          <h3
            className={`font-normal text-heading p-h1-body font-cambonLight ${
              isHome ? '!text-[#330000]' : 'text-white'
            } ${isMobile ? 'text-sm' : 'text-[24px]'}`}
          >
            {title} <br />
            <span className='font-cambonLightItalic font-normal !text-[#330000]'>
              {italicTitle}
            </span>
          </h3>
          <p
            className={`font-barlowMedium ${
              isMobile ? 'text-xs' : 'text-[14px]'
            } max-w-[80%] mx-auto tracking-[0.2px]`}
          >
            {description}
          </p>
        </div>
      </div>
    </div>
  )
}

export default VertelierExperienceEngagement
